export interface CachedPdfRecord {
  pdfId: string;
  fileName: string;
  blob: Blob;
  updatedAt: number;
}

export interface LayerMutationRecord {
  id: string;
  createdAt: number;
  operation: 'create' | 'update' | 'delete';
  layerId?: string;
  layerDocumentId: string;
  payload: Record<string, unknown>;
}

const DB_NAME = 'pdf_editor_offline_v1';
const DB_VERSION = 1;
const PDF_STORE = 'pdf_cache';
const OUTBOX_STORE = 'layer_outbox';

const openOfflineDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PDF_STORE)) {
        db.createObjectStore(PDF_STORE, { keyPath: 'pdfId' });
      }
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open offline DB'));
  });
};

const withStore = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => Promise<T>
): Promise<T> => {
  const db = await openOfflineDb();
  try {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    return await operation(store);
  } finally {
    db.close();
  }
};

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
};

export const cachePdfBlob = async (pdfId: string, fileName: string, blob: Blob): Promise<void> => {
  await withStore(PDF_STORE, 'readwrite', async (store) => {
    await requestToPromise(
      store.put({
        pdfId,
        fileName,
        blob,
        updatedAt: Date.now(),
      } satisfies CachedPdfRecord)
    );
  });
};

export const getCachedPdfBlob = async (pdfId: string): Promise<CachedPdfRecord | null> => {
  return withStore(PDF_STORE, 'readonly', async (store) => {
    const result = await requestToPromise<CachedPdfRecord | undefined>(store.get(pdfId));
    return result ?? null;
  });
};

export const enqueueLayerMutation = async (record: LayerMutationRecord): Promise<void> => {
  await withStore(OUTBOX_STORE, 'readwrite', async (store) => {
    await requestToPromise(store.put(record));
  });
};

export const listLayerMutations = async (): Promise<LayerMutationRecord[]> => {
  return withStore(OUTBOX_STORE, 'readonly', async (store) => {
    return requestToPromise<LayerMutationRecord[]>(store.getAll());
  });
};

export const removeLayerMutation = async (mutationId: string): Promise<void> => {
  await withStore(OUTBOX_STORE, 'readwrite', async (store) => {
    await requestToPromise(store.delete(mutationId));
  });
};

export const makeMutationId = (): string => {
  return `mutation_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
